import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { getAnswer1 } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageFAQs/__internal__/LoggedOutCategoryPageC1Answer1Content";
import { ICategory } from "@faire/web-api--source/indigofair/data/ICategory";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization";
import { AccordionItem } from "@faire/web/slate-deprecated/Accordion";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { History } from "history";
import React from "react";

// All StrictLocalMsg below have been explicitly discussed offline regarding the usage of the placeholders and their
// usage in translation will be checked thoroughly
const getQuestion1 = (
  category: ICategory,
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize(
    {
      id: "FWBWmE",
      defaultMessage:
        "What {categoryName} products can I buy on Faire at wholesale prices for my store?",
      description: {
        text: 'first question in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      categoryName: category.name,
    })
  );

const getQuestion2 = (
  category: ICategory,
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize(
    {
      id: "9Gur/F",
      defaultMessage:
        "How can I buy {categoryName} products at wholesale prices for my store?",
      description: {
        text: 'second question in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      categoryName: category.name,
    })
  );

const getAnswer2 = (history: History, category: ICategory) => (
  <Typography>
    <StrictLocalMsg
      id="8RfiJa"
      defaultMessage="Whether you’re buying products for a brick-and-mortar store, online shop, or pop-up shop, you can shop {categoryName} products at wholesale prices on Faire—<SignUpLinkFormatting>sign up for free</SignUpLinkFormatting>. When you join as a retailer, you’ll unlock wholesale prices with thousands of independent brands. You’ll also get exclusive shopping benefits, including free returns on all opening orders and the option to buy now, pay later. <LearnMoreLinkFormatting>Learn more</LearnMoreLinkFormatting>."
      description={{
        text: 'second answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      }}
      values={{
        ...DEPRECATED_backwardsCompatibleValues<Record<string, string>>({
          categoryName: category.name,
        }),
        SignUpLinkFormatting: (chunks: React.ReactNode) => (
          <Button
            variant="tertiary"
            onClick={() =>
              popSignUp(history, SignUpType.GENERIC, () => null, {
                element: SignUpElement.BELOW_FOLD,
                sourceId: "category_page_faq_answer_2",
              })
            }
          >
            {chunks}
          </Button>
        ),
        LearnMoreLinkFormatting: (chunks: React.ReactNode) => (
          <Button
            variant="tertiary"
            linkTo={supportArticle("360015892572")}
            target="_blank"
          >
            {chunks}
          </Button>
        ),
      }}
    />
  </Typography>
);

const getQuestion3 = (
  category: ICategory,
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize(
    {
      id: "xcuHh/",
      defaultMessage:
        "How do I find different {categoryName} brands that sell wholesale to retailers like me?",
      description: {
        text: 'third question in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      categoryName: category.name,
    })
  );

const getAnswer3 = (category: ICategory) => (
  <Typography>
    <StrictLocalMsg
      id="Mbi3AF"
      defaultMessage="Faire’s platform consolidates brands that sell {categoryName} products at wholesale prices to retailers onto a single site, making it possible to buy from independent brands you might not have come across yet. Just a quick browse through Faire’s {categoryName} listings can offer you a range of styles that are perfect for your store."
      description={{
        text: 'third answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      }}
      values={DEPRECATED_backwardsCompatibleValues({
        categoryName: category.name,
      })}
    />
  </Typography>
);

export const getC1CategoryFAQs = (
  history: History,
  category: ICategory,
  subCategories: ICategory[] | undefined,
  strictLocalize: StrictLocalizeFunction
): AccordionItem[] => {
  const faqItems = [
    {
      question: getQuestion1(category, strictLocalize),
      answer: getAnswer1(history, category, subCategories),
    },
    {
      question: getQuestion2(category, strictLocalize),
      answer: getAnswer2(history, category),
    },
    {
      question: getQuestion3(category, strictLocalize),
      answer: getAnswer3(category),
    },
  ];
  return faqItems.filter((faqItem) => faqItem.question && faqItem.answer);
};

// Variant FAQ content for FE_BATCH_11_CATEGORY_FAQS_250708 experiment
export const getVariantC1CategoryFAQs = (
  history: History,
  category: ICategory,
  subCategories: ICategory[] | undefined,
  strictLocalize: StrictLocalizeFunction
): AccordionItem[] => {
  const faqItems = [
    {
      question: getQuestion1(category, strictLocalize),
      answer: getAnswer1(history, category, subCategories),
    },
    {
      question: strictLocalize({
        id: "S/oGf/",
        defaultMessage: "What is Faire?",
        description: {
          text: "First question in variant FAQ content for category page experiment",
        },
      }),
      answer: (
        <Typography>
          <StrictLocalMsg
            id="Pmex/D"
            defaultMessage="Faire is a wholesale marketplace that connects retailers and brands. We help independent businesses discover and buy unique products from thousands of brands, all in one place."
            description={{
              text: "First answer in variant FAQ content for category page experiment",
            }}
          />
        </Typography>
      ),
    },
    {
      question: strictLocalize({
        id: "32oCQx",
        defaultMessage: "Who can buy on Faire?",
        description: {
          text: "Second question in variant FAQ content for category page experiment",
        },
      }),
      answer: (
        <Typography>
          <StrictLocalMsg
            id="Cqcjxr"
            defaultMessage="Faire is designed for retailers looking to source products for their stores. If you own a retail business, sign up to discover and buy wholesale products from brands all around the globe."
            description={{
              text: "Second answer in variant FAQ content for category page experiment",
            }}
          />
        </Typography>
      ),
    },
    {
      question: strictLocalize({
        id: "kMBmVB",
        defaultMessage: "Why can't I see product prices?",
        description: {
          text: "Third question in variant FAQ content for category page experiment",
        },
      }),
      answer: (
        <Typography>
          <StrictLocalMsg
            id="FBR9Jn"
            defaultMessage="Wholesale prices are exclusive to retailers who <SignUpLinkFormatting>sign up with Faire</SignUpLinkFormatting>. By creating a free account, you'll gain access to these prices, along with the ability to browse the complete selection of products. This helps ensure that only verified retailers can view wholesale pricing, protecting the integrity of the brands on Faire."
            description={{
              text: "Third answer in variant FAQ content for category page experiment",
            }}
            values={{
              SignUpLinkFormatting: (chunks: React.ReactNode) => (
                <Button
                  variant="tertiary"
                  onClick={() =>
                    popSignUp(history, SignUpType.GENERIC, () => null, {
                      element: SignUpElement.BELOW_FOLD,
                      sourceId: "category_page_faq_variant_answer_3",
                    })
                  }
                >
                  {chunks}
                </Button>
              ),
            }}
          />
        </Typography>
      ),
    },
    {
      question: strictLocalize({
        id: "6Io7We",
        defaultMessage: "Do I need to meet order minimums?",
        description: {
          text: "Fourth question in variant FAQ content for category page experiment",
        },
      }),
      answer: (
        <Typography>
          <StrictLocalMsg
            id="GBzirG"
            defaultMessage="Each brand sets its own order minimums, but Faire has many brands with low or no minimums, making it easy to test new products or start small. You'll see these details when you're logged into your account and browsing."
            description={{
              text: "Fourth answer in variant FAQ content for category page experiment",
            }}
          />
        </Typography>
      ),
    },
    {
      question: strictLocalize({
        id: "msUnb3",
        defaultMessage: "Does Faire offer payment terms?",
        description: {
          text: "Fifth question in variant FAQ content for category page experiment",
        },
      }),
      answer: (
        <Typography>
          <StrictLocalMsg
            id="49E5P2"
            defaultMessage="Yes! Eligible retailers get access to 60-day payment terms, an interest-free payment option that allows you to buy new inventory now and pay for it 60 days later. There is no fee for using this benefit—we offer terms so you can buy the right amount of inventory for your store, while managing your cash flow."
            description={{
              text: "Fifth answer in variant FAQ content for category page experiment",
            }}
          />
        </Typography>
      ),
    },
    {
      question: strictLocalize({
        id: "Mc8fIa",
        defaultMessage: "Can I return products?",
        description: {
          text: "Sixth question in variant FAQ content for category page experiment",
        },
      }),
      answer: (
        <Typography>
          <StrictLocalMsg
            id="FzbQej"
            defaultMessage="Faire offers free returns on all first orders with a brand, so you can try new product lines with confidence."
            description={{
              text: "Sixth answer in variant FAQ content for category page experiment",
            }}
          />
        </Typography>
      ),
    },
  ];
  return faqItems.filter((faqItem) => faqItem.question && faqItem.answer);
};
